import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Plus, Edit, Trash2, Users, UserCog, Mail, Calendar, Eye, KeyRound, Copy, Check, Image, Settings, MapPin, FileText } from 'lucide-react';
import { toast } from 'sonner';
import DataPagination from '../components/DataPagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Clinics() {
  const { isSuperAdmin, user } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [activeTab, setActiveTab] = useState('clinics');
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    image_base_url: '',
    aws_access_key_id: '',
    aws_secret_access_key: '',
    aws_s3_bucket: '',
    aws_s3_region: '',
    jotform_api_key: '',
    jotform_patient_form_id: ''
  });
  
  const [adminFormData, setAdminFormData] = useState({
    email: '',
    full_name: '',
    location_ids: []
  });
  
  // Locations for the selected clinic
  const [clinicLocations, setClinicLocations] = useState([]);
  
  // Pagination state for clinics
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Pagination state for admin users
  const [adminCurrentPage, setAdminCurrentPage] = useState(1);
  const [adminPageSize, setAdminPageSize] = useState(20);
  const [adminTotalItems, setAdminTotalItems] = useState(0);
  const [adminTotalPages, setAdminTotalPages] = useState(0);

  useEffect(() => {
    fetchClinics();
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdminUsers();
    }
  }, [activeTab, adminCurrentPage, adminPageSize]);

  const fetchClinics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/clinics`, { 
        params: { page: currentPage, page_size: pageSize } 
      });
      const paginatedData = response.data;
      setClinics(paginatedData.items || []);
      setTotalItems(paginatedData.total || 0);
      setTotalPages(paginatedData.total_pages || 0);
    } catch (error) {
      toast.error('Failed to fetch clinics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      setLoadingAdmins(true);
      const response = await axios.get(`${API_URL}/api/users`, { 
        params: { 
          page: adminCurrentPage, 
          page_size: adminPageSize,
          role: 'clinic_admin'
        } 
      });
      const paginatedData = response.data;
      setAdminUsers(paginatedData.items || []);
      setAdminTotalItems(paginatedData.total || 0);
      setAdminTotalPages(paginatedData.total_pages || 0);
    } catch (error) {
      toast.error('Failed to fetch admin users');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleAdminPageChange = (page) => {
    setAdminCurrentPage(page);
  };

  const handleAdminPageSizeChange = (size) => {
    setAdminPageSize(size);
    setAdminCurrentPage(1);
  };

  // Get clinic name by ID
  const getClinicName = (clinicId) => {
    const clinic = clinics.find(c => c.id === clinicId);
    return clinic ? clinic.name : 'Unknown Clinic';
  };

  const handleCreateClinic = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/clinics`, formData);
      toast.success('Clinic created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchClinics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create clinic');
    }
  };

  const handleUpdateClinic = async (e) => {
    e.preventDefault();
    try {
      // Update basic clinic info
      const basicData = {
        name: formData.name,
        slug: formData.slug,
        code: formData.code || null,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        is_active: true
      };
      await axios.put(`${API_URL}/api/clinics/${selectedClinic.id}`, basicData);
      
      // Update settings (includes S3 config) via settings endpoint
      const settingsData = {};
      
      // Add image_base_url if changed
      if (formData.image_base_url !== (selectedClinic.image_base_url || '')) {
        settingsData.image_base_url = formData.image_base_url || null;
      }
      
      // Add AWS credentials if provided (only if user entered new values)
      if (formData.aws_access_key_id) {
        settingsData.aws_access_key_id = formData.aws_access_key_id;
      }
      if (formData.aws_secret_access_key) {
        settingsData.aws_secret_access_key = formData.aws_secret_access_key;
      }
      if (formData.aws_s3_bucket) {
        settingsData.aws_s3_bucket = formData.aws_s3_bucket;
      }
      if (formData.aws_s3_region) {
        settingsData.aws_s3_region = formData.aws_s3_region;
      }
      // JotForm per-clinic credentials (only send if provided)
      if (formData.jotform_api_key) {
        settingsData.jotform_api_key = formData.jotform_api_key;
      }
      if (formData.jotform_patient_form_id) {
        settingsData.jotform_patient_form_id = formData.jotform_patient_form_id;
      }
      
      // Only call settings endpoint if we have data to update
      if (Object.keys(settingsData).length > 0) {
        await axios.put(`${API_URL}/api/clinics/${selectedClinic.id}/settings`, settingsData);
      }
      
      toast.success('Clinic updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchClinics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update clinic');
    }
  };

  const handleDeleteClinic = async (clinicId) => {
    if (!window.confirm('Are you sure you want to delete this clinic? This will deactivate the clinic and all associated data.')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/clinics/${clinicId}`);
      toast.success('Clinic deleted successfully');
      fetchClinics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete clinic');
    }
  };

  const [showAdminCreatedDialog, setShowAdminCreatedDialog] = useState(false);
  const [createdAdminData, setCreatedAdminData] = useState(null);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/users`, {
        email: adminFormData.email,
        full_name: adminFormData.full_name,
        role: 'clinic_admin',
        clinic_id: selectedClinic.id,
        location_ids: adminFormData.location_ids?.length > 0 ? adminFormData.location_ids : null
      });
      
      // Show the created admin dialog with password
      setCreatedAdminData(response.data);
      setShowAdminDialog(false);
      setShowAdminCreatedDialog(true);
      setAdminFormData({ email: '', full_name: '', location_ids: [] });
      setClinicLocations([]);
      // Refresh admin list
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create clinic admin');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/impersonate/${userId}`);
      const { access_token, user: impersonatedUser, clinic } = response.data;
      
      // Store the token and redirect
      localStorage.setItem('clinic_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success(`Now viewing as ${impersonatedUser.full_name}`);
      
      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reset the password for ${userName}? They will need to use the new password to log in.`)) {
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/users/${userId}/reset-provisional-password`);
      setResetPasswordData({
        userName,
        password: response.data.provisional_password
      });
      setShowResetPasswordDialog(true);
      setCopiedPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const openEditDialog = (clinic) => {
    setSelectedClinic(clinic);
    setFormData({
      name: clinic.name,
      slug: clinic.slug,
      code: clinic.code || '',
      address: clinic.address || '',
      phone: clinic.phone || '',
      email: clinic.email || '',
      image_base_url: clinic.image_base_url || '',
      aws_access_key_id: '', // Don't pre-fill credentials for security
      aws_secret_access_key: '',
      aws_s3_bucket: clinic.aws_s3_bucket || '',
      aws_s3_region: clinic.aws_s3_region || 'us-east-1',
      jotform_api_key: '', // Don't pre-fill credentials for security
      jotform_patient_form_id: clinic.jotform_patient_form_id || ''
    });
    setShowEditDialog(true);
  };

  const fetchClinicLocations = async (clinicId) => {
    try {
      const response = await axios.get(`${API_URL}/api/clinics/${clinicId}/locations`);
      setClinicLocations(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch clinic locations:', error);
      setClinicLocations([]);
    }
  };

  const openAdminDialog = async (clinic) => {
    setSelectedClinic(clinic);
    setAdminFormData({ email: '', full_name: '', location_ids: [] });
    setClinicLocations([]);
    setShowAdminDialog(true);
    // Fetch locations for this clinic
    await fetchClinicLocations(clinic.id);
  };

  const toggleAdminLocation = (locationId) => {
    setAdminFormData(prev => {
      const currentIds = prev.location_ids || [];
      if (currentIds.includes(locationId)) {
        return { ...prev, location_ids: currentIds.filter(id => id !== locationId) };
      } else {
        return { ...prev, location_ids: [...currentIds, locationId] };
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      image_base_url: '',
      aws_access_key_id: '',
      aws_secret_access_key: '',
      aws_s3_bucket: '',
      aws_s3_region: '',
      jotform_api_key: '',
      jotform_patient_form_id: ''
    });
    setSelectedClinic(null);
  };

  // Generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  if (!isSuperAdmin()) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500">Access denied. Super Admin only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinics-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Platform Management</h2>
          <p className="text-muted-foreground">Manage clinics and administrator accounts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-clinic-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Clinic
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-blue-50 border border-blue-100">
          <TabsTrigger value="clinics" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Clinics ({totalItems})
          </TabsTrigger>
          <TabsTrigger value="admins" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 flex items-center gap-2">
            <UserCog className="h-4 w-4" />
            Clinic Admins ({adminTotalItems})
          </TabsTrigger>
        </TabsList>

        {/* Clinics Tab */}
        <TabsContent value="clinics">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Clinics
              </CardTitle>
              <CardDescription>
                {totalItems} clinic(s) registered in the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-4">Loading...</div>
              ) : clinics.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No clinics yet. Create your first clinic to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Config</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clinics.map((clinic) => (
                      <TableRow key={clinic.id} data-testid={`clinic-row-${clinic.id}`}>
                        <TableCell className="font-medium">{clinic.name}</TableCell>
                        <TableCell className="text-muted-foreground">{clinic.slug}</TableCell>
                    <TableCell>{clinic.address || '-'}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {clinic.phone && <div>{clinic.phone}</div>}
                        {clinic.email && <div className="text-muted-foreground">{clinic.email}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant={clinic.has_email_configured ? 'default' : 'outline'} className="text-xs w-fit">
                          <Mail className="h-3 w-3 mr-1" />
                          {clinic.has_email_configured ? 'Email' : 'No Email'}
                        </Badge>
                        <Badge variant={clinic.image_base_url ? 'default' : 'outline'} className="text-xs w-fit">
                          <Image className="h-3 w-3 mr-1" />
                          {clinic.has_s3_configured ? 'S3' : 'No S3'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={clinic.is_active ? 'default' : 'secondary'}>
                        {clinic.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAdminDialog(clinic)}
                          data-testid={`add-admin-btn-${clinic.id}`}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Add Admin
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(clinic)}
                          data-testid={`edit-clinic-btn-${clinic.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClinic(clinic.id)}
                          data-testid={`delete-clinic-btn-${clinic.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Pagination for clinics */}
          {!loading && clinics.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Admin Users Tab */}
        <TabsContent value="admins">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCog className="h-5 w-5" />
                Clinic Administrators
              </CardTitle>
              <CardDescription>
                {adminTotalItems} clinic admin(s) created by you
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAdmins ? (
                <div className="text-center py-4">Loading...</div>
              ) : adminUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No clinic admins yet. Create a clinic and add an admin to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Clinic</TableHead>
                      <TableHead>Locations</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminUsers.map((admin) => (
                      <TableRow key={admin.id} data-testid={`admin-row-${admin.id}`}>
                        <TableCell className="font-medium">{admin.full_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {admin.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {clinics.find(c => c.id === admin.clinic_id)?.name || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {admin.location_ids && admin.location_ids.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {(admin.location_names || []).map((name, idx) => (
                                <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">All Locations</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={admin.is_active ? "default" : "secondary"}>
                            {admin.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-muted-foreground text-sm">
                            <Calendar className="h-4 w-4" />
                            {new Date(admin.created_at).toLocaleDateString()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetPassword(admin.id, admin.full_name)}
                              data-testid={`reset-password-btn-${admin.id}`}
                              title="Reset password"
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Impersonate this admin
                                handleImpersonate(admin.id);
                              }}
                              data-testid={`impersonate-admin-btn-${admin.id}`}
                              title="View as this admin"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Impersonate
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination for admins */}
              {!loadingAdmins && adminUsers.length > 0 && (
                <DataPagination
                  currentPage={adminCurrentPage}
                  totalPages={adminTotalPages}
                  totalItems={adminTotalItems}
                  pageSize={adminPageSize}
                  onPageChange={handleAdminPageChange}
                  onPageSizeChange={handleAdminPageSizeChange}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Clinic Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-clinic-dialog">
          <DialogHeader>
            <DialogTitle>Create New Clinic</DialogTitle>
            <DialogDescription>
              Add a new clinic to the platform
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateClinic}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Clinic Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    });
                  }}
                  placeholder="Enter clinic name"
                  required
                  data-testid="clinic-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="clinic-slug"
                  required
                  data-testid="clinic-slug-input"
                />
                <p className="text-xs text-muted-foreground">URL-friendly identifier</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Clinic Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. TCF"
                  data-testid="clinic-code-input"
                />
                <p className="text-xs text-muted-foreground">Short readable code used in patient intake links (e.g. /patient-intake?clinic=TCF)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter address"
                  data-testid="clinic-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                    data-testid="clinic-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="clinic@email.com"
                    data-testid="clinic-email-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-clinic-btn">Create Clinic</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Clinic Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-clinic-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Clinic</DialogTitle>
            <DialogDescription>
              Update clinic information and configuration
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateClinic}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Clinic Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter clinic name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-slug">Slug *</Label>
                <Input
                  id="edit-slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="clinic-slug"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-code">Clinic Code</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. TCF"
                  data-testid="edit-clinic-code-input"
                />
                <p className="text-xs text-muted-foreground">Used in patient intake links: /patient-intake?clinic=TCF</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="clinic@email.com"
                  />
                </div>
              </div>
              
              {/* Image Storage Configuration */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Image className="h-4 w-4 text-blue-600" />
                  <Label className="text-sm font-medium">Image Storage Configuration</Label>
                </div>
                
                {/* CloudFront URL */}
                <div className="space-y-2 mb-4">
                  <Label htmlFor="edit-image-base-url" className="text-sm text-muted-foreground">
                    CloudFront / CDN URL
                  </Label>
                  <Input
                    id="edit-image-base-url"
                    value={formData.image_base_url}
                    onChange={(e) => setFormData({ ...formData, image_base_url: e.target.value })}
                    placeholder="https://d39z2137znorkl.cloudfront.net"
                    data-testid="image-base-url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    CloudFront or CDN URL for serving images publicly. Leave empty to use direct S3 URL.
                  </p>
                </div>
                
                {/* AWS S3 Credentials */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <Settings className="h-4 w-4 text-slate-600" />
                    <Label className="text-sm font-medium text-slate-700">AWS S3 Credentials</Label>
                    {selectedClinic?.has_s3_configured && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Configured</span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="aws-bucket" className="text-xs text-slate-600">S3 Bucket Name</Label>
                      <Input
                        id="aws-bucket"
                        value={formData.aws_s3_bucket}
                        onChange={(e) => setFormData({ ...formData, aws_s3_bucket: e.target.value })}
                        placeholder="my-clinic-bucket"
                        className="text-sm"
                        data-testid="aws-bucket-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="aws-region" className="text-xs text-slate-600">Region</Label>
                      <Input
                        id="aws-region"
                        value={formData.aws_s3_region}
                        onChange={(e) => setFormData({ ...formData, aws_s3_region: e.target.value })}
                        placeholder="us-east-1"
                        className="text-sm"
                        data-testid="aws-region-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="aws-access-key" className="text-xs text-slate-600">Access Key ID</Label>
                    <Input
                      id="aws-access-key"
                      value={formData.aws_access_key_id}
                      onChange={(e) => setFormData({ ...formData, aws_access_key_id: e.target.value })}
                      placeholder={selectedClinic?.has_s3_configured ? "••••••••••••••••" : "AKIA..."}
                      className="text-sm font-mono"
                      data-testid="aws-access-key-input"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label htmlFor="aws-secret-key" className="text-xs text-slate-600">Secret Access Key</Label>
                    <Input
                      id="aws-secret-key"
                      type="password"
                      value={formData.aws_secret_access_key}
                      onChange={(e) => setFormData({ ...formData, aws_secret_access_key: e.target.value })}
                      placeholder={selectedClinic?.has_s3_configured ? "••••••••••••••••••••••" : "Enter secret key"}
                      className="text-sm font-mono"
                      data-testid="aws-secret-key-input"
                    />
                  </div>
                  
                  <p className="text-xs text-slate-500">
                    Credentials are stored securely and never exposed in responses. Leave blank to keep existing credentials.
                  </p>
                </div>

                {/* JotForm Credentials (per-clinic) */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-600" />
                    <Label className="text-sm font-medium text-slate-700">JotForm (Patient Intake Submissions)</Label>
                    {selectedClinic?.has_jotform_configured && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Configured</span>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="jotform-form-id" className="text-xs text-slate-600">Form ID</Label>
                    <Input
                      id="jotform-form-id"
                      value={formData.jotform_patient_form_id}
                      onChange={(e) => setFormData({ ...formData, jotform_patient_form_id: e.target.value })}
                      placeholder="e.g. 240357176844058"
                      className="text-sm font-mono"
                      data-testid="jotform-form-id-input"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="jotform-api-key" className="text-xs text-slate-600">API Key (HIPAA)</Label>
                    <Input
                      id="jotform-api-key"
                      type="password"
                      value={formData.jotform_api_key}
                      onChange={(e) => setFormData({ ...formData, jotform_api_key: e.target.value })}
                      placeholder={selectedClinic?.has_jotform_configured ? "••••••••••••••••" : "Enter JotForm API key"}
                      className="text-sm font-mono"
                      data-testid="jotform-api-key-input"
                    />
                  </div>

                  <p className="text-xs text-slate-500">
                    Each clinic uses its own JotForm account. When unset, this clinic's "JotForm Submissions" menu is hidden. Leave the API key blank to keep the existing one.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Update Clinic</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent data-testid="create-admin-dialog">
          <DialogHeader>
            <DialogTitle>Create Clinic Admin</DialogTitle>
            <DialogDescription>
              Create an admin user for {selectedClinic?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAdmin}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="admin-name">Full Name *</Label>
                <Input
                  id="admin-name"
                  value={adminFormData.full_name}
                  onChange={(e) => setAdminFormData({ ...adminFormData, full_name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  data-testid="admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email *</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminFormData.email}
                  onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                  placeholder="admin@clinic.com"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-500" />
                  Assigned Locations
                </Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto bg-slate-50" data-testid="admin-locations-select">
                  {clinicLocations.length === 0 ? (
                    <p className="text-sm text-slate-500">No locations available for this clinic</p>
                  ) : (
                    <>
                      {clinicLocations.map((loc) => (
                        <label 
                          key={loc.id} 
                          className="flex items-center gap-2 cursor-pointer hover:bg-white p-1.5 rounded transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={adminFormData.location_ids?.includes(loc.id) || false}
                            onChange={() => toggleAdminLocation(loc.id)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">{loc.name}</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {adminFormData.location_ids?.length > 0 
                    ? `Admin will see data from ${adminFormData.location_ids.length} selected location(s)` 
                    : "No locations selected - admin will have access to all clinic locations."}
                </p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm">
                  A temporary password will be auto-generated and shown after creation.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdminDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-admin-btn">Create Admin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Admin Created Dialog */}
      <Dialog open={showAdminCreatedDialog} onOpenChange={setShowAdminCreatedDialog}>
        <DialogContent data-testid="admin-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Users className="w-5 h-5" />
              Clinic Admin Created
            </DialogTitle>
            <DialogDescription>
              Admin for {selectedClinic?.name} has been created successfully
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
              <div>
                <p className="text-xs text-green-600 uppercase tracking-wider font-medium">Name</p>
                <p className="font-medium">{createdAdminData?.full_name}</p>
              </div>
              <div>
                <p className="text-xs text-green-600 uppercase tracking-wider font-medium">Email</p>
                <p className="font-medium">{createdAdminData?.email}</p>
              </div>
              <div>
                <p className="text-xs text-green-600 uppercase tracking-wider font-medium">Temporary Password</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border border-green-200" data-testid="admin-generated-password">
                    {createdAdminData?.provisional_password}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdAdminData?.provisional_password || '');
                      toast.success('Password copied to clipboard');
                    }}
                    className="border-green-300"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-sm text-amber-600">
              Share these credentials securely. The admin will be required to change their password on first login.
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowAdminCreatedDialog(false);
                setCreatedAdminData(null);
              }} 
              className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md"
              data-testid="admin-created-done-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <KeyRound className="w-5 h-5" />
              Password Reset Complete
            </DialogTitle>
            <DialogDescription>
              New password generated for {resetPasswordData?.userName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div>
                <p className="text-xs text-amber-600 uppercase tracking-wider font-medium">New Temporary Password</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border border-amber-200" data-testid="reset-password-value">
                    {resetPasswordData?.password}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(resetPasswordData?.password || '')}
                    className="border-amber-300"
                  >
                    {copiedPassword ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <p className="text-sm text-amber-600">
              Share this password securely with the user. They will be required to change it on next login.
            </p>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowResetPasswordDialog(false);
                setResetPasswordData(null);
              }} 
              className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md"
              data-testid="reset-password-done-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
